import type { ParseKeys } from "i18next";
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';

import CustomerPageLayout from '../../../customer-page-layout/CustomerPageLayout';
import { BuyElectricityConfirmation } from '../buy-electricity-confirmation';
import { BuyElectricityTransactionPin } from '../transaction-pin';
import { PaymentMethodSelection, CustomerPinModal, SuccessScreen, TransactionResultPage, CustomerLoader } from '../../../components';
import { TransactionResultPageV2 } from '../../../components/transaction-result-v2';

import { RootState } from '../../../../../stores/store';
import { CountryCode, countryCurrencies } from '../../../../../model/common/country';
import { VerifyPostpaidMeterResponse } from '../../../../../model/charge';
import { useLazyVerifyPostpaidMeterQuery, useLazyVerifyPrepaidMeterQuery } from '../../../../../service/vending-service';
import { useGetPaymentFeeMutation } from '../../../../../service/charge-service';
import { useLazyGetPrepaidTransactionByReferenceForCustomerQuery } from '../../../../../service/transaction-service';
import { useGetUserInfoQuery } from '../../../../../service/user-service';
import { useVendingOutcomeEvents } from '../../../../../hooks/useVendingOutcomeEvents';
import { describeError } from '../../../../../services/business-analytics/customFields';
import {
	BillingFailureReason,
	trackBillingFailed,
	trackBillingSuccess,
	trackBillPaymentStarted,
	trackContinueToPay,
	trackMeterValidationFailed,
	trackMeterValidationSuccess,
	trackPaymentMethodSelected,
} from '../../../../../services/business-analytics/vendEvents';
import {
	clearTransaction,
	setPaymentMethod,
	setTransactionDetails,
	setTransactionPaymentType,
	setTransactionStatus,
} from '../../../../../stores/slices/buy-electricity-slice';

import {
	BuyElectricityConfirmationData,
	BuyElectricityNavigationState,
	BuyElectricityPurchaseType,
	PaymentOptions,
} from '../../buy-electricity.types';

import styles from '../../buy-electricity.module.scss';
import successGif from '../../../../../assets/gifs/success-gif.gif'

type NotificationFlowView = 'confirmation' | 'payment' | 'pin' | 'success' | 'failed';

const PAYMENT_METHOD_LABEL_KEYS: Record<PaymentOptions, ParseKeys> = {
	[PaymentOptions.Card]:        'buyElectricity.web.debitCreditCard',
	[PaymentOptions.Wallet]:      'common.labels.wallet',
	[PaymentOptions.Bank]:        'buyElectricity.web.bankTransfer',
	[PaymentOptions.Mono]:        'buyElectricity.web.openBankingMono',
	[PaymentOptions.BorrowToken]: 'buyElectricity.web.borrowUnits',
	[PaymentOptions.Transfer]:    'buyElectricity.web.payWithTransfer',
	[PaymentOptions.Cash]:        'buyElectricity.web.cash',
	[PaymentOptions.AskAFriendToPay]: 'payment.askAFriendToPay',
};

interface BuyElectricityNotificationFlowProps {
	notificationState: BuyElectricityNavigationState;
}

export const BuyElectricityNotificationFlow: React.FC<BuyElectricityNotificationFlowProps> = ({ notificationState }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const { data: userInfo } = useGetUserInfoQuery();
	const energyProfile = useSelector((state: RootState) => state.customerEnergyProfile);
	const currentTransaction = useSelector((state: RootState) => state.buyElectricity);
	const { countryCode } = useSelector((state: RootState) => state.user);

	const [verifyPostpaidMeter] = useLazyVerifyPostpaidMeterQuery();
	const [verifyPrepaidMeter] = useLazyVerifyPrepaidMeterQuery();
	const [getPaymentFee] = useGetPaymentFeeMutation();
	const [getTransaction, { isLoading: isLoadingTransaction }] = useLazyGetPrepaidTransactionByReferenceForCustomerQuery();
	const reportVendingOutcome = useVendingOutcomeEvents();

	const { meterNumber, amountToPay, paymentType, purchaseType, billingDashNotificationId } = notificationState;
	const amount = amountToPay || '0';
	const currency = countryCurrencies[countryCode as CountryCode];

	const [currentView, setCurrentView] = useState<NotificationFlowView>('confirmation');
	const [showResetPin, setShowResetPin] = useState(false);
	const [confirmationData, setConfirmationData] = useState<BuyElectricityConfirmationData>({
		paymentType,
		meterNumber,
		amount,
		customerName: '',
		disco: '',
		currency,
		outstandingBalance: amountToPay ? Number(amountToPay) : undefined,
		outstandingDebt: 0,
		showPreferRemoteTokenLoadOption: false,
	});
	const [isEnriching, setIsEnriching] = useState(true);
	const [selectedPaymentOption, setSelectedPaymentOption] = useState<PaymentOptions>(PaymentOptions.Card);
	const [fullTransaction, setFullTransaction] = useState<any | null>(null);

	useEffect(() => {
		// This flow skips the form, so it starts with the meter type it was opened with.
		trackBillPaymentStarted(paymentType);
		const enrich = async () => {
			try {
				const verifyFn = paymentType === 'prepaid' ? verifyPrepaidMeter : verifyPostpaidMeter;
				const [meterResponse, feeResponse] = await Promise.all([
					verifyFn({ MeterNumber: meterNumber, UtilityId: energyProfile.utilityId ?? '' }).unwrap() as Promise<VerifyPostpaidMeterResponse>,
					Number(amount) > 0 ? getPaymentFee({ amount: Number(amount), currency }).unwrap() : Promise.resolve(null),
				]);

				setConfirmationData(prev => ({
					...prev,
					customerName: meterResponse.accountName ?? '',
					meterAddress: meterResponse.address ?? undefined,
					outstandingBalance: (meterResponse as VerifyPostpaidMeterResponse).outstandingBalance ?? prev.outstandingBalance,
					disco: energyProfile.utility?.name ?? '',
					phoneNumber: userInfo?.phoneNumber ?? '',
					fee: feeResponse?.estimationFees[0]?.paymentChannels[0]?.totalFee?.amount,
				}));

				dispatch(setTransactionDetails({ amount, meterNumber, utilityId: energyProfile.utilityId }));
				dispatch(setTransactionPaymentType(paymentType));
				trackMeterValidationSuccess(meterNumber, paymentType);
			} catch (err) {
				console.error('[BuyElectricityNotificationFlow] enrichment failed:', err);
				trackMeterValidationFailed(meterNumber, describeError(err));
			} finally {
				setIsEnriching(false);
			}
		};

		enrich();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const getTotalAmount = (): number => {
		const base = Number.parseFloat(confirmationData.amount);
		return base + (confirmationData.fee ?? 0);
	};

	const handlePaymentContinue = (paymentMethod: PaymentOptions) => {
		setSelectedPaymentOption(paymentMethod);
		dispatch(setPaymentMethod(t(PAYMENT_METHOD_LABEL_KEYS[paymentMethod])));
		// The amount is fixed by the notification, so "continue to pay" is this same click.
		trackContinueToPay(amount);
		trackPaymentMethodSelected(paymentMethod);
		setCurrentView('pin');
		// Mark payment view as touched for back-handler routing consistency
		void 'payment';
	};

	const handlePaymentSuccess = async (reference: string) => {
		trackBillingSuccess(amount, reference);
		if (paymentType === 'prepaid') {
			try {
				const result = await getTransaction({ Reference: reference }).unwrap();
				if (result) {
					setFullTransaction(result);
					reportVendingOutcome(result);
				}
			} catch (error) {
				console.error('Failed to fetch transaction:', error);
				toast.error(t('buyElectricity.web.failedToLoadTransactionDetails'));
			}
		}
		setCurrentView('success');
	};

	const handlePaymentFailure = (reference: string, reason: BillingFailureReason = 'payment_failed') => {
		trackBillingFailed(reason, reference);
		dispatch(setTransactionStatus('failed'));
		setCurrentView('failed');
	};

	const getBackHandler = useCallback((): (() => void) => {
		if (currentView === 'confirmation') return () => navigate(-1);
		if (currentView === 'payment') return () => setCurrentView('confirmation');
		if (currentView === 'pin') return () => setCurrentView('payment');
		return () => navigate(-1);
	}, [currentView, navigate]);

	const showBackButton = currentView !== 'success' && currentView !== 'failed';

	return (
		<CustomerPageLayout
			title={t('common.labels.buyElectricity')}
			onBack={getBackHandler()}
			showBackButton={showBackButton}
		>
			<div className={styles.pageContainer}>
				{isEnriching ? (
					<CustomerLoader title={t('buyElectricity.web.loadingDetails')} />
				) : (
				<>
				{currentView === 'confirmation' && (
					<BuyElectricityConfirmation
						data={confirmationData}
						totalAmount={getTotalAmount()}
						onClose={() => navigate(-1)}
						onPay={handlePaymentContinue}
						availableMethods={[
							PaymentOptions.Card,
							PaymentOptions.Wallet,
						]}
						walletAvailableBalance={0}
					/>
				)}

				{currentView === 'pin' && !showResetPin && (
					<BuyElectricityTransactionPin
						confirmationData={{
							utilityId: energyProfile.utilityId,
							meterNumber: confirmationData.meterNumber,
							amount: confirmationData.amount,
							paymentType,
							showPreferRemoteTokenLoadOption: false,
						}}
						selectedPaymentOption={selectedPaymentOption}
						handleBack={() => setCurrentView('payment')}
						onPaymentSuccess={handlePaymentSuccess}
						onPaymentFailed={handlePaymentFailure}
						onForgetPin={() => setShowResetPin(true)}
						onBorrowSuccess={() => {}}
						billingDashNotificationId={purchaseType === BuyElectricityPurchaseType.Exact ? billingDashNotificationId : undefined}
					/>
				)}

				{currentView === 'pin' && showResetPin && (
					<CustomerPinModal
						initialContent="resetPinContent"
						showResetSuccessScreen
						onClose={() => setShowResetPin(false)}
					/>
				)}

				{currentView === 'success' && paymentType === 'prepaid' ? (
					<TransactionResultPageV2
						transaction={fullTransaction ?? undefined}
						isLoading={isLoadingTransaction}
						currency={countryCode === CountryCode.Nigeria ? 'NGN' : 'USD'}
						paymentMethod={currentTransaction.paymentMethod ?? undefined}
						onContinue={() => {
							dispatch(clearTransaction());
							navigate('/dashboard');
						}}
						onCancel={() => {
							dispatch(clearTransaction());
							navigate('/buy-electricity');
						}}
						onReportIssue={() => {
							navigate('/support/report-issue', {
								state: {
									reference: fullTransaction?.reference,
									amount: fullTransaction?.amount?.value,
									transactionStatus: 'completed',
								},
							});
						}}
					/>
				) : currentView === 'success' ? (
					<SuccessScreen
						title={t('common.labels.transactionCompleted')}
						buttonText={t('common.actions.done')}
						subtitle=""
						imageSrc={successGif}
						onContinue={() => {
							dispatch(clearTransaction());
							navigate('/dashboard');
						}}
					/>
				) : null}

				{currentView === 'failed' && (
					<TransactionResultPage
						transaction={{
							status: (currentTransaction.status as 'failed' | 'pending') || 'failed',
							reference: currentTransaction.reference || t('buyElectricity.web.notAvailable'),
							amount: parseFloat(currentTransaction.amount || '0'),
							paymentMethod: currentTransaction.paymentMethod || t('buyElectricity.web.notAvailable'),
							date: currentTransaction.date || new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
							time: currentTransaction.time || new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
						}}
						onRetry={() => setCurrentView('confirmation')}
						onDone={() => {
							dispatch(clearTransaction());
							navigate('/dashboard');
						}}
					/>
				)} </>)}
				</div>
		</CustomerPageLayout>
	);
};
